import { useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@core/dialog'
import Tooltip from '@core/tooltip'

import { useFetchNotificationProfiles } from '@components/control-panel/report-scheduler/queries/notification-profiles/fetch-notification-profiles'
import Button from '@components/core/button'
import { Icon, Trash } from '@components/icons'

import useAuth from '@hooks/useAuth'

export interface IActionData {
  id: number
  notificationProfileId: number
  email: string
}

interface SendReportCellRendererProps {
  data: IActionData
  onAction: (data: IActionData, sendToProfile: boolean) => void
  actionType: 'dialog' | 'custom'
  confirmationDialogTitle?: string
  confirmationDialogDescription?: string
  iconName?: React.ReactNode
}

export function SendReportCellRenderer({
  data,
  onAction,
  actionType,
  confirmationDialogTitle = 'Dialog Title',
  confirmationDialogDescription = 'Please define your action here',
  iconName = <Icon icon={<Trash />} />
}: SendReportCellRendererProps) {
  const { user } = useAuth()

  const [showDialog, setShowDialog] = useState(false)
  const { data: notificationProfiles } = useFetchNotificationProfiles()

  const matchedProfile = useMemo(() => {
    return notificationProfiles?.find((p) => p.id === data.notificationProfileId)
  }, [notificationProfiles, data.notificationProfileId])

  const internalUsers = matchedProfile?.internalUsers || []

  const handleActionClick = () => {
    setShowDialog(true)
  }

  const handleConfirmAction = (sendToProfile: boolean) => {
    if (onAction) {
      onAction(data, sendToProfile)
    }

    setShowDialog(false)
  }

  return (
    <div className='flex size-full items-center'>
      <Button
        size='action-bar'
        variant='text'
        className='rounded-[4px] p-0 focus:border-hidden'
        onClick={handleActionClick}
      >
        {iconName}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className='min-w-[500px]'>
          <DialogHeader>
            <DialogTitle>{confirmationDialogTitle}</DialogTitle>
            <DialogDescription>{confirmationDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className='mt-4 flex gap-4'>
            <div className='w-1/2'>
              <div className='font-medium'>Current User:</div>
              <div className='text-xs h-[100px] w-full overflow-y-auto rounded border p-2'>
                <div>
                  <Tooltip title={user?.email}>
                    <div className='font-medium'>{user?.full_name}</div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className='w-1/2'>
              <div className='font-medium'>Notification Profile Users:</div>
              <div className='text-xs h-[100px] w-full overflow-y-auto rounded border p-2'>
                {internalUsers.map((internalUser: any) => (
                  <div key={internalUser?.id} className='mb-1'>
                    <Tooltip title={internalUser?.email}>
                      <div className='font-medium'>{internalUser.name}</div>
                    </Tooltip>
                  </div>
                ))}
                {!internalUsers.length && <div>No users found</div>}
              </div>
            </div>
          </div>

          <div className='mt-4 flex justify-between'>
            <Button variant='primary' className='w-48' onClick={() => handleConfirmAction(false)}>
              Send to User
            </Button>
            <Button
              disabled={!internalUsers.length}
              variant={!internalUsers.length ? 'disabled' : 'primary'}
              className='w-48'
              onClick={() => handleConfirmAction(true)}
            >
              Send to Notification Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
